import Emittery from 'emittery';
import Maybe from 'true-myth/maybe';

import type { CompanyProfile } from './company-profile';
import type { CVAnalysisResponse } from './cv-analysis.factories';
import type { FetchCompanyProfile } from './fetch-company-profile';
import type { FetchCvAnalysis } from './fetch-cv-analysis';
import type { GenerateCompanyDescription } from './generate-company-description';
import type { GenerateCompanyPriorities } from './generate-company-priorities';
import type { UpdateCompanyProfile } from './update-company-profile';
import type { UpdateUserSettings } from './update-user-settings';
import type { UploadCV } from './upload-cv';
import type { UserSettingsPayload } from './user-settings';
import type { Clock } from '../../../shared/source/clock/clock';
import type { CreateNewJobArchitecture } from '../job-architecture/create-new-job-architecture';
import type { GenerateJobFamiliesWithAi } from '../job-architecture/generate-job-families-with-ai';
import type { SendJobArchitectureMetadata } from '../job-architecture/send-job-architecture-metadata';

export const refreshCompanyProfileEvent = Symbol('refresh:company-profile');
export const refreshedCompanyProfileEvent = Symbol('refreshed:company-profile');

export const updateCompanyProfileEvent = Symbol('update:company-profile');
export const updatedCompanyProfileEvent = Symbol('updated:company-profile');

export const updateCompanyDescriptionEvent = Symbol('update:company-description');
export const updatedCompanyDescriptionEvent = Symbol('updated:company-description');

export const generateCompanyDescriptionEvent = Symbol('generate:company-description');
export const generatedCompanyDescriptionEvent = Symbol('generated:company-description');

export const generateCompanyPrioritiesEvent = Symbol('generate:company-priorities');
export const generatedCompanyPrioritiesEvent = Symbol('generated:company-priorities');

export const fetchCvAnalysisEvent = Symbol('fetch:cv-analysis');
export const fetchedCvAnalysisEvent = Symbol('fetched:cv-analysis');

export const uploadCvEvent = Symbol('upload:cv');
export const uploadedCvEvent = Symbol('uploaded:cv');

export const generateJobArchitectureEvent = Symbol('generate:job-architecture');
export const generatedJobArchitectureEvent = Symbol('generated:job-architecture');

export const updateUserSettingsEvent = Symbol('update:user-settings');
export const updatedUserSettingsEvent = Symbol('updated:user-settings');

type GeneratedJobArchitectureEventEventPayload = {
    readonly companyDescription: string;
    readonly companyPriorities: string;
};

type OnEventAndPayload = {
    readonly [generatedCompanyDescriptionEvent]: CompanyProfile;
    readonly [generatedCompanyPrioritiesEvent]: CompanyProfile;
    readonly [generatedJobArchitectureEvent]: undefined;
    readonly [refreshedCompanyProfileEvent]: CompanyProfile;
    readonly [uploadedCvEvent]: { readonly id: string };
    readonly [fetchedCvAnalysisEvent]: CVAnalysisResponse;
    readonly [updatedCompanyProfileEvent]: CompanyProfile;
    readonly [updatedCompanyDescriptionEvent]: CompanyProfile;
    readonly [updatedUserSettingsEvent]: undefined;
};

type EmitEventAndPayload = {
    readonly [fetchCvAnalysisEvent]: string;
    readonly [generateCompanyDescriptionEvent]: void;
    readonly [generateCompanyPrioritiesEvent]: void;
    readonly [generateJobArchitectureEvent]: GeneratedJobArchitectureEventEventPayload;
    readonly [refreshCompanyProfileEvent]: void;
    readonly [uploadCvEvent]: File;
    readonly [updateCompanyProfileEvent]: CompanyProfile;
    readonly [updateCompanyDescriptionEvent]: CompanyProfile;
    readonly [updateUserSettingsEvent]: UserSettingsPayload;
};

export type CompanyOnboardingEmitteryEvents = EmitEventAndPayload & OnEventAndPayload;

export type CompanyOnboardingEmitterDependencies = {
    readonly clock: Clock;
    readonly createNewJobArchitecture: CreateNewJobArchitecture;
    readonly fetchCompanyProfile: FetchCompanyProfile;
    readonly fetchCvAnalysis: FetchCvAnalysis;
    readonly generateCompanyDescription: GenerateCompanyDescription;
    readonly generateCompanyPriorities: GenerateCompanyPriorities;
    readonly generateJobFamilies: GenerateJobFamiliesWithAi;
    readonly sendJobArchitectureMetadata: SendJobArchitectureMetadata;
    readonly updateCompanyProfile: UpdateCompanyProfile;
    readonly updateUserSettings: UpdateUserSettings;
    readonly uploadCV: UploadCV;
};

export type CompanyOnboardingEmitter<TEvents extends EmitEventAndPayload = EmitEventAndPayload> = {
    readonly emit: <TEvent extends keyof TEvents>(eventName: TEvent, payload?: TEvents[TEvent]) => Promise<void>;
    readonly on: Emittery<OnEventAndPayload>['on'];
};

const pollingTimeout = 2000;

export const createCompanyOnboardingEmitter = (
    dependencies: CompanyOnboardingEmitterDependencies,
): CompanyOnboardingEmitter => {
    const {
        clock,
        createNewJobArchitecture,
        fetchCompanyProfile,
        fetchCvAnalysis,
        generateCompanyDescription,
        generateCompanyPriorities,
        generateJobFamilies,
        sendJobArchitectureMetadata,
        updateCompanyProfile,
        updateUserSettings,
        uploadCV,
    } = dependencies;
    const emitter = new Emittery<CompanyOnboardingEmitteryEvents>();
    let cvAnalysesTimeout: number | undefined;

    const refetchCompanyProfile = async (): Promise<void> => {
        const result = await fetchCompanyProfile();

        if (result.isOk) {
            emitter.emit(refreshedCompanyProfileEvent, result.value);
        }
    };

    const refetchCvAnalysis = async (id: string): Promise<void> => {
        clock.clearTimeout(cvAnalysesTimeout);
        const result = await fetchCvAnalysis(id);

        if (result.isOk) {
            emitter.emit(fetchedCvAnalysisEvent, result.value);

            if (result.value.status === 'imported') {
                cvAnalysesTimeout = clock.setTimeout(() => {
                    refetchCvAnalysis(id);
                }, pollingTimeout);
            }
        }
    };

    const uploadUserCV = async (file: File): Promise<void> => {
        const formData = new FormData();
        formData.append('file', file);
        const result = await uploadCV(formData);

        if (result.isOk) {
            refetchCvAnalysis(result.value.id);
        }
    };

    const generateDescription = async (): Promise<void> => {
        const result = await generateCompanyDescription();

        if (result.isOk) {
            emitter.emit(generatedCompanyDescriptionEvent, result.value);
        }
    };

    const generatePriorities = async (): Promise<void> => {
        const result = await generateCompanyPriorities();

        if (result.isOk) {
            emitter.emit(generatedCompanyPrioritiesEvent, result.value);
        }
    };

    const updateProfile = async (companyProfile: CompanyProfile): Promise<void> => {
        const result = await updateCompanyProfile(companyProfile);

        if (result.isOk) {
            emitter.emit(updatedCompanyProfileEvent, result.value);
        }
    };

    const generateJobArchitecture = async ({
        companyDescription,
        companyPriorities,
    }: GeneratedJobArchitectureEventEventPayload): Promise<void> => {
        const jobArchitectureResult = await createNewJobArchitecture();

        if (jobArchitectureResult.isErr) {
            return;
        }

        const metadataResult = await sendJobArchitectureMetadata(jobArchitectureResult.value, {
            careerTrackTypes: Maybe.nothing(),
            companyDescription: Maybe.just(companyDescription),
            companyPriorities: Maybe.just(companyPriorities),
            companySize: Maybe.nothing(),
            executiveRolesInTheirOwnJobFamily: Maybe.nothing(),
        });

        if (metadataResult.isErr) {
            return;
        }

        void generateJobFamilies(jobArchitectureResult.value);

        emitter.emit(generatedJobArchitectureEvent);
    };

    const updateCompanyDescription = async (companyProfile: CompanyProfile): Promise<void> => {
        const result = await updateCompanyProfile(companyProfile);

        if (result.isOk) {
            emitter.emit(updatedCompanyDescriptionEvent, result.value);
        }
    };

    const updateSettings = async (userSettings: UserSettingsPayload): Promise<void> => {
        const result = await updateUserSettings(userSettings);
        if (result.isOk) {
            emitter.emit(updatedUserSettingsEvent);
        }
    };

    const emit: CompanyOnboardingEmitter['emit'] = async (event, payload) => {
        if (event === refreshCompanyProfileEvent) {
            return refetchCompanyProfile();
        }

        if (event === uploadCvEvent) {
            return uploadUserCV(payload as File);
        }

        if (event === generateCompanyDescriptionEvent) {
            return generateDescription();
        }

        if (event === generateCompanyPrioritiesEvent) {
            return generatePriorities();
        }

        if (event === fetchCvAnalysisEvent) {
            return refetchCvAnalysis(payload as string);
        }

        if (event === updateCompanyProfileEvent) {
            return updateProfile(payload as CompanyProfile);
        }

        if (event === generateJobArchitectureEvent) {
            return generateJobArchitecture(payload as GeneratedJobArchitectureEventEventPayload);
        }
        if (event === updateCompanyDescriptionEvent) {
            return updateCompanyDescription(payload as CompanyProfile);
        }
        if (event === updateUserSettingsEvent) {
            return updateSettings(payload as UserSettingsPayload);
        }
    };

    return {
        emit,
        on: emitter.on.bind(emitter),
    };
};
